import React, { useState, useEffect } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Autocomplete, TextField, CircularProgress } from '@mui/material';
import useSupabaseClient from '../hooks/useSupabaseClient';

// Limitar opciones
import { createFilterOptions } from '@mui/material/Autocomplete';
const filterOptionsLimit = createFilterOptions({
  limit: 50,
  stringify: (option) => typeof option === 'string' ? option : `${option.account_code || option.codigo} ${option.description || option.descripcion}`
});

const AccountOverrideForm = ({ includeInstructions, setIncludeInstructions }) => {
  const { supabase } = useSupabaseClient();
  const [gastosAccount, setGastosAccount] = useState(null);
  const [impuestosAccount, setImpuestosAccount] = useState(null);
  const [retencionesAccount, setRetencionesAccount] = useState(null);
  const [proveedoresAccount, setProveedoresAccount] = useState(null);

  const [gastosSearch, setGastosSearch] = useState('');
  const [impuestosSearch, setImpuestosSearch] = useState('');
  const [retencionesSearch, setRetencionesSearch] = useState('');
  const [proveedoresSearch, setProveedoresSearch] = useState('');

  const [gastosOptions, setGastosOptions] = useState([]);
  const [impuestosOptions, setImpuestosOptions] = useState([]);
  const [retencionesOptions, setRetencionesOptions] = useState([]);
  const [proveedoresOptions, setProveedoresOptions] = useState([]);

  const [isSearchingGastos, setIsSearchingGastos] = useState(false);
  const [isSearchingImpuestos, setIsSearchingImpuestos] = useState(false);
  const [isSearchingRetenciones, setIsSearchingRetenciones] = useState(false);
  const [isSearchingProveedores, setIsSearchingProveedores] = useState(false);

  const [openGastos, setOpenGastos] = useState(false);
  const [openImpuestos, setOpenImpuestos] = useState(false);
  const [openRetenciones, setOpenRetenciones] = useState(false);
  const [openProveedores, setOpenProveedores] = useState(false);

  useEffect(() => {
    try {
      const savedGastos = localStorage.getItem('override_gastos');
      const savedImpuestos = localStorage.getItem('override_impuestos');
      const savedRetenciones = localStorage.getItem('override_retenciones');
      const savedProveedores = localStorage.getItem('override_proveedores');
      if (savedGastos) setGastosAccount(JSON.parse(savedGastos));
      if (savedImpuestos) setImpuestosAccount(JSON.parse(savedImpuestos));
      if (savedRetenciones) setRetencionesAccount(JSON.parse(savedRetenciones));
      if (savedProveedores) setProveedoresAccount(JSON.parse(savedProveedores));
    } catch (e) {
      console.error("Error loading overrides from localStorage", e);
    }
  }, []);

  const handleSaveToLocalStorage = (key, value) => {
    if (value) {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      localStorage.removeItem(key);
    }
  };

  const searchAccounts = async (term) => {
    if (!supabase) return [];
    const t = String(term || '').trim();
    if (!t) return [];

    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return [];
    let user;
    try {
      user = JSON.parse(storedUser);
    } catch {
      return [];
    }
    if (!user?.id) return [];

    try {
      const { data, error } = await supabase.rpc('search_accounts_by_prefix', {
        search_term: t,
        p_user_id: user.id
      });
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error("Error searching accounts via RPC:", err);
      return [];
    }
  };

  useEffect(() => {
    if (!openGastos) return;
    setIsSearchingGastos(true);
    const t = setTimeout(async () => {
      const opts = await searchAccounts(gastosSearch);
      setGastosOptions(opts);
      setIsSearchingGastos(false);
    }, 300);
    return () => clearTimeout(t);
  }, [gastosSearch, openGastos]);

  useEffect(() => {
    if (!openImpuestos) return;
    setIsSearchingImpuestos(true);
    const t = setTimeout(async () => {
      const opts = await searchAccounts(impuestosSearch);
      setImpuestosOptions(opts);
      setIsSearchingImpuestos(false);
    }, 300);
    return () => clearTimeout(t);
  }, [impuestosSearch, openImpuestos]);

  useEffect(() => {
    if (!openRetenciones) return;
    setIsSearchingRetenciones(true);
    const t = setTimeout(async () => {
      const opts = await searchAccounts(retencionesSearch);
      setRetencionesOptions(opts);
      setIsSearchingRetenciones(false);
    }, 300);
    return () => clearTimeout(t);
  }, [retencionesSearch, openRetenciones]);

  useEffect(() => {
    if (!openProveedores) return;
    setIsSearchingProveedores(true);
    const t = setTimeout(async () => {
      const opts = await searchAccounts(proveedoresSearch);
      setProveedoresOptions(opts);
      setIsSearchingProveedores(false);
    }, 300);
    return () => clearTimeout(t);
  }, [proveedoresSearch, openProveedores]);

  const renderAutocomplete = (label, value, setValue, search, setSearch, options, isSearching, open, setOpen, storageKey) => (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(event, newValue) => {
        setValue(newValue);
        handleSaveToLocalStorage(storageKey, newValue);
      }}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input') setSearch(newInputValue);
        if (reason === 'clear') setSearch('');
      }}
      isOptionEqualToValue={(option, val) => option.account_code === val.account_code}
      getOptionLabel={(option) => typeof option === 'string' ? option : `${option.account_code || option.codigo || ''} - ${option.description || option.descripcion || ''}`}
      options={options}
      loading={isSearching}
      filterOptions={filterOptionsLimit}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          sx={{ mb: 2 }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );

  return (
    <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>Configuración de Cuentas (Opcional)</Typography>
      
      {renderAutocomplete("Cuenta de Gastos", gastosAccount, setGastosAccount, gastosSearch, setGastosSearch, gastosOptions, isSearchingGastos, openGastos, setOpenGastos, 'override_gastos')}
      {renderAutocomplete("Cuenta de Impuestos (IVA)", impuestosAccount, setImpuestosAccount, impuestosSearch, setImpuestosSearch, impuestosOptions, isSearchingImpuestos, openImpuestos, setOpenImpuestos, 'override_impuestos')}
      {renderAutocomplete("Cuenta de Retenciones", retencionesAccount, setRetencionesAccount, retencionesSearch, setRetencionesSearch, retencionesOptions, isSearchingRetenciones, openRetenciones, setOpenRetenciones, 'override_retenciones')}
      {renderAutocomplete("Cuenta de Proveedores", proveedoresAccount, setProveedoresAccount, proveedoresSearch, setProveedoresSearch, proveedoresOptions, isSearchingProveedores, openProveedores, setOpenProveedores, 'override_proveedores')}

      <FormControlLabel
        control={
          <Checkbox
            checked={includeInstructions}
            onChange={(e) => {
                setIncludeInstructions(e.target.checked);
                localStorage.setItem('invoiceIncludeInstructions', e.target.checked.toString());
            }}
            color="primary"
          />
        }
        label="Sobrescribir las cuentas de la IA con las selecciones anteriores al enviar los archivos"
        sx={{ mt: 0 }}
      />
    </Box>
  );
};

export default AccountOverrideForm;
